import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { 
  ArrowRight, 
  ArrowLeft,
  Users, 
  Lightbulb, 
  Building,
  ClipboardCheck,
  Rocket,
  Network,
  Target,
  CheckCircle
} from "lucide-react";

export default function AspiringLanding() {
  const navigate = useNavigate();

  const features = [
    {
      icon: Building,
      title: "Explore Venture Builders",
      description: "Discover UK venture builders like Entrepreneur First, Conception, and BioCity Labs. Understand their programs, investment thesis, and application process."
    },
    {
      icon: Users,
      title: "Find Your Cofounder",
      description: "Connect with complementary founders in the UK biotech ecosystem. Match based on skills, experience, and shared therapeutic interests."
    },
    {
      icon: Target,
      title: "Validate Your Idea",
      description: "Explore market opportunities in therapeutics. Understand indication landscapes, competitive dynamics, and unmet needs."
    },
    {
      icon: Lightbulb,
      title: "Define Your Solution",
      description: "Structure your technology approach. Identify your modality, understand development pathways, and articulate your defensibility."
    },
    {
      icon: ClipboardCheck,
      title: "Assess Your Readiness",
      description: "Get an AI-powered founder readiness assessment. Identify strengths, risks, and areas to focus on before making the jump."
    },
    {
      icon: Rocket,
      title: "Resources to Launch",
      description: "Access guides, templates, and resources for starting a UK therapeutics company. From incorporation to first funding."
    }
  ];

  const ventureBuilders = [
    { name: "Entrepreneur First", focus: "Deep tech & biotech", type: "Talent investor" },
    { name: "Conception", focus: "Life sciences spinouts", type: "Venture builder" },
    { name: "BioCity Labs", focus: "Biotech incubation", type: "Incubator" },
    { name: "Start Codon", focus: "Cambridge life sciences", type: "Accelerator" },
    { name: "Nucleate UK", focus: "Student biotech", type: "Community" },
  ];

  const journeySteps = [
    { step: "1", title: "Create your profile", description: "Define your background, skills, and what you bring to a founding team" },
    { step: "2", title: "Explore or find cofounders", description: "Connect with complementary founders or define your ideal cofounder" },
    { step: "3", title: "Shape your idea", description: "Identify market opportunities and validate your therapeutic focus" },
    { step: "4", title: "Define your solution", description: "Structure your technology approach and articulate your edge" },
    { step: "5", title: "Get assessed", description: "AI-powered readiness check with actionable feedback" },
    { step: "6", title: "Make the jump", description: "Access resources and take the next step" },
  ];

  return (
    <div className="min-h-screen bg-[#121318]">
      {/* Navigation */}
      <nav className="max-w-7xl mx-auto w-full px-6 py-6 flex items-center justify-between">
        <Button 
          onClick={() => navigate('/')}
          variant="ghost"
          size="icon"
          className="text-slate-400 hover:text-white hover:bg-slate-800"
        >
          <ArrowLeft className="w-5 h-5" />
        </Button>
        
        <div className="flex items-center gap-2">
          <img 
            src="/logo-complete.png" 
            alt="Operon" 
            className="h-[50px] object-contain"
          />
          <span className="text-purple-400 text-xl font-semibold">Start</span>
        </div>
        
        <div className="w-10" /> {/* Spacer for centering */}
      </nav>

      {/* Hero Section */}
      <section className="max-w-7xl mx-auto px-6 py-12 md:py-16">
        <div className="text-center max-w-4xl mx-auto">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6">
            Start your journey in{" "}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-pink-400">
              UK Therapeutics
            </span>
          </h1>
          
          <p className="text-lg sm:text-xl text-slate-400 mb-10 max-w-2xl mx-auto">
            Navigate the UK biotech ecosystem. Find venture builders, connect with cofounders, 
            validate your idea, and assess your readiness to start a therapeutics company.
          </p>

          <Button 
            onClick={() => navigate('/aspiring/auth')}
            className="bg-purple-500 hover:bg-purple-600 text-white px-8 py-6 h-auto text-lg rounded-xl"
            data-testid="btn-sign-in"
          >
            Sign In
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      </section>

      {/* Venture Builders Highlight */}
      <section className="border-y border-slate-700 bg-slate-800/30">
        <div className="max-w-7xl mx-auto px-6 py-12">
          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold text-white mb-2">
              Explore UK Venture Builders & Programs
            </h2>
            <p className="text-slate-400">
              Discover the organizations helping founders build therapeutics companies
            </p>
          </div>
          <div className="flex flex-wrap justify-center gap-4">
            {ventureBuilders.map((vb, i) => (
              <div 
                key={i}
                className="px-5 py-3 rounded-xl bg-slate-800/50 border border-slate-700 hover:border-purple-500/50 transition-colors"
              >
                <div className="font-medium text-white">{vb.name}</div>
                <div className="text-xs text-slate-400">{vb.focus} · {vb.type}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="max-w-7xl mx-auto px-6 py-20">
        <div className="text-center mb-12">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
            Everything you need to explore starting a company
          </h2>
          <p className="text-slate-400 max-w-2xl mx-auto">
            Operon guides you through the key questions and decisions aspiring 
            therapeutics founders face.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {features.map((feature, i) => {
            const Icon = feature.icon;
            return (
              <div 
                key={i}
                className="p-6 rounded-2xl bg-slate-800/50 border border-slate-700 hover:border-purple-500/50 transition-colors"
              >
                <div className="w-12 h-12 rounded-xl bg-purple-500/10 flex items-center justify-center mb-4">
                  <Icon className="w-6 h-6 text-purple-400" />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">{feature.title}</h3>
                <p className="text-slate-400 text-sm">{feature.description}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* Journey Steps */}
      <section className="max-w-7xl mx-auto px-6 py-16">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-white mb-4">
            Your founder journey, step by step
          </h2>
          <p className="text-slate-400">
            Work through the key stages at your own pace
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {journeySteps.map((step, i) => (
            <div 
              key={i}
              className="flex gap-4 p-5 rounded-xl bg-slate-800/30 border border-slate-700"
            >
              <div className="w-10 h-10 rounded-full bg-purple-500/20 flex items-center justify-center flex-shrink-0">
                <span className="text-purple-400 font-bold">{step.step}</span>
              </div>
              <div>
                <h3 className="font-semibold text-white mb-1">{step.title}</h3>
                <p className="text-sm text-slate-400">{step.description}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA Section */}
      <section className="max-w-7xl mx-auto px-6 py-20">
        <div className="bg-gradient-to-r from-purple-500/20 to-pink-500/20 rounded-3xl p-8 md:p-12 border border-purple-500/30 text-center">
          <Network className="w-12 h-12 text-purple-400 mx-auto mb-4" />
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
            Ready to explore?
          </h2>
          <p className="text-slate-300 mb-8 max-w-xl mx-auto">
            Create your profile, explore the ecosystem, and take the first steps 
            toward building a therapeutics company.
          </p>
          <Button 
            onClick={() => navigate('/aspiring/auth')}
            className="bg-purple-500 hover:bg-purple-600 text-white px-8 py-6 h-auto text-lg rounded-xl"
            data-testid="btn-cta-aspiring"
          >
            Get Started Free
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-slate-700 py-8">
        <div className="max-w-7xl mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-4">
          <img 
            src="/logo-complete.png" 
            alt="Operon" 
            className="h-[72px] object-contain"
          />
          <div className="flex flex-col md:flex-row items-center gap-4">
            <Button 
              onClick={() => navigate('/founders')}
              variant="ghost"
              className="text-slate-400 hover:text-white text-sm"
            >
              Already running a company? Go to Founder Platform
            </Button>
            <p className="text-sm text-slate-500">
              © {new Date().getFullYear()} Operon. Built for UK biotech founders.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
